import React, { useEffect, useState } from 'react';
import { Stack, Paper, Grid, TextField, Button, Typography, Checkbox, FormControlLabel, FormControl, InputLabel, MenuItem, Select } from '@mui/material/';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';

//components imports
import AlertInfo from '../../../dataObjects/AlertInfo';
import AlertInfoDialog from '../../../components/elements/AlertInfoDialog';
import DropdownOption from '../../../dataObjects/DropdownOption';
import { GenericFormObject } from '../dataObjects/GenericFormObject';


interface FormDataProps {
    formData: GenericFormObject;
    closeGenericForm: () => void;
}

const BankAppointmentForm = (props: FormDataProps): JSX.Element => {

    /*Error Handling*/
    const [isAlertInfoDialogOpen, setAlertInfoDialog] = useState(false);
    const [alertInfo, setAlertInfo] = useState<AlertInfo>();

    /*Initial State*/
    const [form, setForm] = useState<GenericFormObject>(props.formData);
    const [banks, setBanks] = useState<DropdownOption[]>([]);
    const [bankId, setBankId] = useState<number>(-1);
    const [create200BankLetter, setCreate200BankLetter] = useState<boolean>(true);
    const [create200D103Letter, setCreate200D103Letter] = useState<boolean>(true);

    useEffect(() => {
        setForm(props.formData);
    }, [props.formData]);

    useEffect(() => {
        fetch('/reference/GetDepositoryBankList')
            .then(res => res.json())
            .then(data => {
                let bankList = data;
                setBanks(bankList);
            });
    }, []);

    //after getting the bank list, populate the bank id
    useEffect(() => {
        if (form.bank?.bankName && banks.length > 0) {
            const formBankName = form.bank.bankName.toLowerCase();
            const match = banks.find((b: DropdownOption) =>
                b.Display.toLowerCase() === formBankName ||
                b.Display.toLowerCase().includes(formBankName) ||
                formBankName.includes(b.Display.toLowerCase())
            );
            if (match) {
                setBankId(Number(match.Value));
            }
        }
    }, [banks, form.bank?.bankName]);

    
    const closeAlertInfoDialog = () => {
        setAlertInfoDialog(false);
        if (alertInfo?.type === 'success') {
            props.closeGenericForm();
        }
    }

    const saveNewBankInfo = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const formData = new FormData(event.currentTarget);
        formData.append("cpfId", form.cpfID);
        formData.append("create200D103Letter", create200D103Letter.toString());
        formData.append("create200BankLetter", create200BankLetter.toString());
        formData.append("url", form.formIntakeBlobUrl);

        fetch(`/LLMFormProcessor/AppointBank`,
            {
                method: 'POST',
                body: formData,
            })
            .then(result => result.json())
            .then(result => {
                if (result.isError) {
                    let info = {
                        type: 'error',
                        title: 'Error: SaveNewBankInfo',
                        errorMessage: result.errorMessage,
                        secondaryErrorMessage: result.secondaryErrorMessage
                    };
                    setAlertInfo(info);
                    setAlertInfoDialog(true);
                }
                else {
                    let info = {
                        type: 'success',
                        title: 'The New Bank Info Saved',
                        errorMessage: 'The new bank info was saved successfully!\nThe draft archer letters were created in Archer.',
                        secondaryErrorMessage: ''
                    };
                    setAlertInfo(info);
                    setAlertInfoDialog(true);
                    //open profiles??
                }
            });
    }

    return (
        <>
            <form onSubmit={(event: React.FormEvent<HTMLFormElement>) => saveNewBankInfo(event)}>
                <Paper sx={{ p: 3 }} >
                    <Grid item xs={12} marginTop={2} >
                        <Stack spacing={2}>
                            <Typography variant="subtitle1" fontWeight={550} >D103 Form Info</Typography>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker
                                    sx={{ minWidth: 100, width: 400 }}
                                    name="effectiveDate"
                                    label="Effective Audit Start Date (D103 Date/Account Opened Date)"
                                    defaultValue={dayjs(form.bank?.accountOpenDate)}
                                    slotProps={{ textField: { required: true } }}
                                />
                            </LocalizationProvider>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker
                                    sx={{ minWidth: 100, width: 400 }}
                                    name="BankSignedDate"
                                    label="Bank Representative Signature Date (used in Bank Letter)"
                                    defaultValue={dayjs(form.bank?.date)}
                                />
                            </LocalizationProvider>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker
                                    sx={{ minWidth: 100, width: 400 }}
                                    name="receivedDate"
                                    label="D103 Received Date"
                                    defaultValue={dayjs()}
                                />
                            </LocalizationProvider>
                            <TextField
                                margin="normal"
                                name="depositoryBankName"
                                label="Bank Name (from the form, for reference)"
                                size="small"
                                defaultValue={form.bank?.bankName}
                            />
                            <FormControl sx={{ m: 1, minWidth: 120 }} size="small" error={bankId === -1} >
                                <InputLabel>Depository Bank</InputLabel>
                                <Select
                                    name="depositoryBankId"
                                    label="Depository Bank"
                                    value={bankId}
                                    onChange={(event) => setBankId(Number(event.target.value))}
                                >
                                    <MenuItem value={-1}>Please select one</MenuItem>
                                    {
                                        banks && banks.map((bank) => (
                                            <MenuItem key={bank.Value} value={bank.Value}>{bank.Value} {bank.Display}</MenuItem>
                                        ))
                                    }
                                </Select>
                            </FormControl>      
                            <FormControlLabel
                                control={<Checkbox checked={create200D103Letter} color="primary" onChange={(e) => setCreate200D103Letter(e.target.checked)} />}
                                label={`Create a Bank Appointment Received Acknowledgement letter addressed to the filer?`}
                            />
                            <FormControlLabel
                                control={<Checkbox checked={create200BankLetter} color="primary" onChange={(e) => setCreate200BankLetter(e.target.checked)} />}
                                label={`Create a Bank New Filer Archer letter addressed to the new bank?`}
                            />
                        </Stack>
                    </Grid>
                </Paper>
                <Grid item xs={12} marginTop={3} marginBottom={3} >
                    <Stack direction="row" spacing={2} justifyContent={"flex-end"} marginTop={2} >
                        <Button variant="contained" color="warning" onClick={props.closeGenericForm}>Cancel</Button>
                        <Button variant="contained" type="submit">Save</Button>
                    </Stack>
                </Grid>
            </form>
            {
                alertInfo &&
                <AlertInfoDialog isAlertInfoDialogOpen={isAlertInfoDialogOpen} alertInfo={alertInfo} closeAlertInfoDialog={closeAlertInfoDialog}></AlertInfoDialog>
            }
        </>
    )
}

export default BankAppointmentForm;